// =====================================================================
// CadastrosPendentes (S86) — fila de cadastro de paciente p/ aprovação humana.
//
// A IA Clara (número oficial) NÃO coleta dado sensível por chat; manda um link de
// formulário. O paciente preenche -> cai aqui (status='pending'). A recepção REVISA
// e APROVA -> vira/atualiza paciente no Prontuário (backend reusa PatientService).
// Multi-tenant: o backend filtra por companyId do JWT (LGPD). Padrão espelhado de
// pages/Prontuario (MUI v4 + layout do CRM).
// =====================================================================
import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  CircularProgress,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Chip,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Divider
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BlockIcon from "@material-ui/icons/Block";
import LinkIcon from "@material-ui/icons/Link";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RefreshIcon from "@material-ui/icons/Refresh";

import useIsMobile from "../../utils/useIsMobile";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import { toast } from "react-toastify";

import {
  listPendingRegistrations,
  approveRegistration,
  rejectRegistration,
  generateRegistrationLink,
  registrationErrorMessage
} from "../../services/patientRegistrationService";

const useStyles = makeStyles((theme) => ({
  mainPaper: { flex: 1, padding: theme.spacing(2), overflowY: "auto", minHeight: 0 },
  toolbar: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2)
  },
  emptyState: { padding: theme.spacing(6), textAlign: "center", color: theme.palette.text.secondary },
  loadingWrap: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 },
  pagination: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2)
  },
  statusChip: { fontWeight: 600, height: 22 },
  fieldLabel: { fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: theme.palette.text.secondary },
  fieldValue: { fontSize: 14, marginBottom: theme.spacing(1.5) },
  linkBox: { wordBreak: "break-all", background: "#f6f6f6", borderRadius: 8, padding: theme.spacing(1.5), fontSize: 13 }
}));

const PAGE_SIZE = 50;

const STATUS_META = {
  pending: { label: "Pendente", color: "#8a6d00", bg: "#fdf3d6" },
  approved: { label: "Aprovado", color: "#1f6b45", bg: "#e6f4ec" },
  rejected: { label: "Rejeitado", color: "#7a7a7a", bg: "#eee" }
};

const formatCpf = (cpf) => {
  if (!cpf) return "—";
  const d = String(cpf).replace(/\D/g, "");
  if (d.length !== 11) return cpf;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const formatPhone = (p) => {
  if (!p) return "—";
  const d = String(p).replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return p;
};

const formatDateBR = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return "—";
  return `${d}/${m}/${y}`;
};

const formatDateTimeBR = (iso) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
};

const formatAddress = (a) => {
  if (!a || typeof a !== "object") return "—";
  const linha1 = [a.logradouro, a.numero].filter(Boolean).join(", ");
  const linha2 = [a.bairro, [a.cidade, a.uf].filter(Boolean).join("/")].filter(Boolean).join(" - ");
  const cep = a.cep ? `CEP ${String(a.cep).replace(/(\d{5})(\d{3})/, "$1-$2")}` : "";
  const parts = [linha1, a.complemento, linha2, cep].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
};

const Field = ({ label, value, classes, full }) => (
  <Grid item xs={12} sm={full ? 12 : 6}>
    <div className={classes.fieldLabel}>{label}</div>
    <div className={classes.fieldValue}>{value || "—"}</div>
  </Grid>
);

const CadastrosPendentes = () => {
  const classes = useStyles();
  const history = useHistory();
  const isMobile = useIsMobile();

  const [registrations, setRegistrations] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // dialog de revisão/aprovação
  const [review, setReview] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // dialog de gerar link
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkPhone, setLinkPhone] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);

  const fetchRegistrations = useCallback(async ({ status: s, page: p }) => {
    setLoading(true);
    try {
      const data = await listPendingRegistrations({ status: s, page: p, pageSize: PAGE_SIZE });
      setRegistrations(Array.isArray(data?.registrations) ? data.registrations : []);
      setCount(Number(data?.count) || 0);
    } catch (err) {
      toast.error("Falha ao carregar os cadastros. Tente novamente.");
      setRegistrations([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchRegistrations({ status, page: 1 });
  }, [status, fetchRegistrations]);

  const totalPages = Math.max(Math.ceil(count / PAGE_SIZE), 1);
  const goPage = (next) => {
    const p = Math.min(Math.max(next, 1), totalPages);
    if (p === page) return;
    setPage(p);
    fetchRegistrations({ status, page: p });
  };

  const openReview = (reg) => {
    setRejectReason("");
    setReview(reg);
  };
  const closeReview = () => setReview(null);

  const handleApprove = async () => {
    if (!review) return;
    setActionLoading(true);
    try {
      const res = await approveRegistration(review.id);
      toast.success(res?.created ? "Paciente cadastrado no Prontuário." : "Paciente já existia — ficha atualizada.");
      closeReview();
      fetchRegistrations({ status, page });
    } catch (err) {
      toast.error(registrationErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!review) return;
    setActionLoading(true);
    try {
      await rejectRegistration(review.id, rejectReason);
      toast.info("Cadastro rejeitado.");
      closeReview();
      fetchRegistrations({ status, page });
    } catch (err) {
      toast.error(registrationErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const maskPhoneInput = (v) => {
    const d = String(v || "").replace(/\D/g, "").slice(0, 11);
    if (d.length > 10) return d.replace(/(\d{2})(\d{5})(\d{1,4})/, "($1) $2-$3");
    if (d.length > 6) return d.replace(/(\d{2})(\d{4})(\d{1,4})/, "($1) $2-$3");
    if (d.length > 2) return d.replace(/(\d{2})(\d{1,5})/, "($1) $2");
    return d;
  };

  const openLinkDialog = () => {
    setLinkPhone("");
    setLinkUrl("");
    setLinkOpen(true);
  };

  const handleGenerateLink = async () => {
    setLinkLoading(true);
    try {
      const res = await generateRegistrationLink(String(linkPhone).replace(/\D/g, ""));
      setLinkUrl(res?.url || "");
    } catch (err) {
      toast.error(registrationErrorMessage(err));
    } finally {
      setLinkLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      toast.success("Link copiado!");
    } catch (e) {
      toast.info("Selecione o link e copie manualmente.");
    }
  };

  const renderStatusChip = (s) => {
    const meta = STATUS_META[s] || { label: s, color: "#555", bg: "#eee" };
    return (
      <Chip
        size="small"
        label={meta.label}
        className={classes.statusChip}
        style={{ color: meta.color, backgroundColor: meta.bg }}
      />
    );
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Cadastros Pendentes</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="outlined" color="primary" startIcon={<LinkIcon />} onClick={openLinkDialog}>
            Gerar link de cadastro
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.toolbar}>
          <Typography variant="body2" color="textSecondary">Mostrar:</Typography>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            variant="outlined"
            margin="dense"
            style={{ minWidth: 160 }}
          >
            <MenuItem value="pending">Pendentes</MenuItem>
            <MenuItem value="approved">Aprovados</MenuItem>
            <MenuItem value="rejected">Rejeitados</MenuItem>
            <MenuItem value="all">Todos</MenuItem>
          </Select>
          <Tooltip title="Atualizar">
            <IconButton size="small" onClick={() => fetchRegistrations({ status, page })}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box flexGrow={1} />
          <Typography variant="body2" color="textSecondary">
            {count} {count === 1 ? "cadastro" : "cadastros"}
          </Typography>
        </div>

        {loading ? (
          <div className={classes.loadingWrap}>
            <CircularProgress />
          </div>
        ) : registrations.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="h6" gutterBottom>
              {status === "pending" ? "Nenhum cadastro aguardando aprovação" : "Nada por aqui"}
            </Typography>
            <Typography variant="body2">
              Os formulários que os pacientes preencherem aparecem aqui para a recepção revisar e aprovar.
            </Typography>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto", width: "100%" }}>
            <Table size="small" style={isMobile ? { minWidth: 640 } : undefined}>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>CPF</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>Nascimento</TableCell>
                  <TableCell>Recebido</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center" style={{ width: 90 }}>Revisar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {registrations.map((r) => (
                  <TableRow key={r.id} hover style={{ cursor: "pointer" }} onClick={() => openReview(r)}>
                    <TableCell>{r.fullName || "—"}</TableCell>
                    <TableCell>{formatCpf(r.cpf)}</TableCell>
                    <TableCell>{formatPhone(r.phone)}</TableCell>
                    <TableCell>{formatDateBR(r.birthDate)}</TableCell>
                    <TableCell>{formatDateTimeBR(r.createdAt)}</TableCell>
                    <TableCell>{renderStatusChip(r.status)}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Revisar e aprovar">
                        <IconButton size="small" onClick={() => openReview(r)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>

            <div className={classes.pagination}>
              <Button size="small" disabled={page <= 1} onClick={() => goPage(page - 1)}>Anterior</Button>
              <Typography variant="body2">Página {page} de {totalPages}</Typography>
              <Button size="small" disabled={page >= totalPages} onClick={() => goPage(page + 1)}>Próxima</Button>
            </div>
          </>
        )}
      </Paper>

      {/* Dialog de revisão / aprovação */}
      <Dialog open={!!review} onClose={closeReview} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Revisar cadastro {review && renderStatusChip(review.status)}</DialogTitle>
        <DialogContent dividers>
          {review && (
            <Grid container spacing={1}>
              <Field classes={classes} label="Nome completo" value={review.fullName} full />
              <Field classes={classes} label="CPF" value={formatCpf(review.cpf)} />
              <Field classes={classes} label="Nascimento" value={formatDateBR(review.birthDate)} />
              <Field classes={classes} label="Telefone" value={formatPhone(review.phone)} />
              <Field classes={classes} label="E-mail" value={review.email} />
              <Field classes={classes} label="Como conheceu" value={review.acquisitionSource} />
              <Field classes={classes} label="Endereço" value={formatAddress(review.address)} full />
              <Grid item xs={12}><Divider style={{ margin: "4px 0 8px" }} /></Grid>
              <Field
                classes={classes}
                label="Consentimento LGPD"
                value={review.consentLgpd ? `Sim — ${formatDateTimeBR(review.consentAt)}` : "Não"}
              />
              <Field classes={classes} label="Recebido em" value={formatDateTimeBR(review.createdAt)} />
              {review.status === "pending" && (
                <Grid item xs={12} style={{ marginTop: 8 }}>
                  <TextField
                    label="Motivo da rejeição (opcional)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReview} disabled={actionLoading}>Fechar</Button>
          {review && review.status === "pending" && (
            <>
              <Button onClick={handleReject} disabled={actionLoading} startIcon={<BlockIcon />} style={{ color: "#c0392b" }}>
                Rejeitar
              </Button>
              <Button
                onClick={handleApprove}
                disabled={actionLoading}
                variant="contained"
                color="primary"
                startIcon={actionLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              >
                Aprovar
              </Button>
            </>
          )}
          {review && review.status === "approved" && review.matchedPatientId && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push(`/prontuario/paciente/${review.matchedPatientId}`)}
            >
              Abrir ficha
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de gerar link */}
      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Gerar link de cadastro</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            Gere um link seguro e envie ao paciente pelo WhatsApp. Ele preenche a ficha e cai aqui para você aprovar.
          </Typography>
          <TextField
            label="WhatsApp do paciente"
            placeholder="(81) 90000-0000"
            value={linkPhone}
            onChange={(e) => setLinkPhone(maskPhoneInput(e.target.value))}
            variant="outlined"
            size="small"
            fullWidth
          />
          {linkUrl && (
            <Box mt={2}>
              <TextField
                label="Link (válido por 72h)"
                value={linkUrl}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
                helperText="Toque para selecionar, ou use o botão Copiar."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Fechar</Button>
          {linkUrl ? (
            <Button variant="contained" color="primary" startIcon={<FileCopyIcon />} onClick={copyLink}>
              Copiar link
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              disabled={linkLoading || String(linkPhone).replace(/\D/g, "").length < 10}
              startIcon={linkLoading ? <CircularProgress size={16} /> : <LinkIcon />}
              onClick={handleGenerateLink}
            >
              Gerar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default CadastrosPendentes;
